import { create } from 'zustand';
import { MIN_TRIM_SECONDS } from '@/constants/media';
import {
  type ConversionPhase,
  type ConversionState,
  type CropRect,
  type GifResult,
  type GifSettings,
  type SourceMeta,
  type TrimRange,
} from '@/types';
import { clamp } from '@/utils/format';

export const DEFAULT_SETTINGS: GifSettings = {
  sizePreset: 'auto',
  customWidth: 480,
  customHeight: 270,
  lockAspect: true,
  fps: 15,
  quality: 'balanced',
  speed: 1,
  loopForever: true,
  dither: 'auto',
  dropDuplicates: false,
};

const IDLE_CONVERSION: ConversionState = {
  phase: 'idle',
  progress: 0,
  etaSeconds: null,
  error: null,
};

export interface ProjectState {
  file: File | null;
  sourceUrl: string | null;
  meta: SourceMeta | null;
  filmstrip: string[];
  trim: TrimRange;
  crop: CropRect | null;
  settings: GifSettings;
  previewLoop: boolean;
  conversion: ConversionState;
  result: GifResult | null;

  setSource: (file: File, sourceUrl: string, meta: SourceMeta) => void;
  setFilmstrip: (frames: string[]) => void;
  setTrim: (trim: Partial<TrimRange>) => void;
  setCrop: (crop: CropRect | null) => void;
  updateSettings: (patch: Partial<GifSettings>) => void;
  setPreviewLoop: (on: boolean) => void;

  conversionStarted: () => void;
  conversionProgress: (progress: number, etaSeconds: number | null, phase: ConversionPhase) => void;
  conversionDone: (result: GifResult) => void;
  conversionFailed: (error: string) => void;
  conversionCancelled: () => void;
  dismissResult: () => void;

  resetAll: () => void;
}

const initialData = {
  file: null,
  sourceUrl: null,
  meta: null,
  filmstrip: [] as string[],
  trim: { start: 0, end: 0 },
  crop: null,
  settings: DEFAULT_SETTINGS,
  previewLoop: true,
  conversion: IDLE_CONVERSION,
  result: null,
};

export const useProjectStore = create<ProjectState>((set) => ({
  ...initialData,

  // Loading a new source resets the edit but deliberately keeps the user's
  // settings — tuned fps/quality choices usually carry across clips.
  setSource: (file, sourceUrl, meta) =>
    set((state) => ({
      file,
      sourceUrl,
      meta,
      filmstrip: [],
      trim: { start: 0, end: meta.duration },
      crop: null,
      previewLoop: true,
      conversion: IDLE_CONVERSION,
      result: null,
      settings: state.settings,
    })),

  setFilmstrip: (frames) => set({ filmstrip: frames }),

  setTrim: (patch) =>
    set((state) => {
      if (!state.meta) return state;
      const duration = state.meta.duration;
      let start = patch.start ?? state.trim.start;
      let end = patch.end ?? state.trim.end;
      start = clamp(start, 0, duration - MIN_TRIM_SECONDS);
      end = clamp(end, MIN_TRIM_SECONDS, duration);
      if (patch.start !== undefined) end = Math.max(end, start + MIN_TRIM_SECONDS);
      else start = Math.min(start, end - MIN_TRIM_SECONDS);
      return { trim: { start, end } };
    }),

  setCrop: (crop) => set({ crop }),

  updateSettings: (patch) => set((state) => ({ settings: { ...state.settings, ...patch } })),

  setPreviewLoop: (on) => set({ previewLoop: on }),

  conversionStarted: () =>
    set({
      conversion: { phase: 'loading-engine', progress: 0, etaSeconds: null, error: null },
    }),

  conversionProgress: (progress, etaSeconds, phase) =>
    set({ conversion: { phase, progress: clamp(progress, 0, 1), etaSeconds, error: null } }),

  conversionDone: (result) =>
    set({
      result,
      conversion: { phase: 'done', progress: 1, etaSeconds: null, error: null },
    }),

  conversionFailed: (error) =>
    set({ conversion: { phase: 'error', progress: 0, etaSeconds: null, error } }),

  conversionCancelled: () =>
    set({ conversion: { phase: 'cancelled', progress: 0, etaSeconds: null, error: null } }),

  dismissResult: () =>
    set((state) => ({
      result: null,
      conversion: state.conversion.phase === 'done' ? IDLE_CONVERSION : state.conversion,
    })),

  resetAll: () => set({ ...initialData }),
}));
